/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * Footer component for PersonalAccount table
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @version 1.14.1
 * @since 1.0.0
 */

import { FC, useCallback, useEffect } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import  filter from 'lodash/filter';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';

import { mobileFileCashSuccessful } from '@/main/components/Table/actions/tableActions';
import { Loader } from '@/main/modules/loader/components/Loader';
import { FastAdvanceInvoiceButtons } from '@/main/pages/FastAdvanceInvoice/FastAdvanceInvoiceButtons';
import { FastInquiryCreateButtons } from '@/main/pages/FastInquiryCreate/FastInquiryCreateButtons';
import { FastPromisedPaymentButtons } from '@/main/pages/FastPromisedPayment/FastPromisedPaymentButtons';
import { openRoute } from '@/main/routes/constants/externalRoutes';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { IPersonalAccount } from '@/main/types/personalAccountTypes';

import useTableFooterButtons, { IUseTableFooterButtons } from '../../../../components/common/TableFooter/hooks/useTableFooterButtons';
import useTableFooterDisabled from '../../../../components/common/TableFooter/hooks/useTableFooterDisabled';
import {
    TableFooterButtonContainerStyled,
    TableFooterContainerStyled,
    TableFooterCounterContainerStyled,
    TableFooterFastActionsButtonsStyled,
    TableFooterLeftContainerStyled,
    TableFooterRightContainerStyled,
    TableFooterRowContainerStyled,
} from '../../../../components/common/TableFooter/styled/TableFooterStyled';
import TableFooterItem from '../../../../components/common/TableFooter/TableFooterItem';
import TableFooterShowMenu from '../../../../components/common/TableFooter/TableFooterShowMenu';
import Basket from '../../../../components/Table/components/footerModal/Basket/components/Basket';
import { ITableReducer } from '../../../../components/Table/reducers/tableReducer';
import {
    makeSelectorIsFastActionPage,
    makeSelectorIsFastBasketPage,
    makeSelectorMobileCashFileIsLoading,
    makeSelectorMobileCashFileIsSuccessful,
    makeSelectorTableBasketCount,
    makeSelectorTableButtons,
    makeSelectorTableData,
    makeSelectorTableIsLoading,
} from '../../../../components/Table/selectors/tableSelectors';
import { TableFooterNameType, TableNameType } from '../../../../components/Table/types/tableTypes';
import {
    EGTMBasketSectionType,
    EGTMBasketValueType,
} from '../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMBasketOperations';
import { makeSelectTranslationPagePersonalAccount } from '../../../../modules/internationalization/intSelectors';
import { IPagePersonalAccountTranslation } from '../../../../modules/internationalization/types';
import Guard from '../../../../modules/role/Guard';
import { IRootReducer } from '../../../../reducers/rootReducer';

interface IPersonalAccountTableFooterProps {
    isOpen?: boolean;
    onClick?: () => void;
}

export const PersonalAccountTableFooter = (
    props: IPersonalAccountTableFooterProps & IConnectedProps,
) => {

    const {
        buttons,
        countBasket,
        isOpen,
        onClick,
        personalAccountTranslation,
    } = props;

    const location = useLocation();
    const dispatch = useDispatch();

    const isMobileCashFileLoading = useSelector(makeSelectorMobileCashFileIsLoading());
    const isMobileCashFileSuccess = useSelector(makeSelectorMobileCashFileIsSuccessful());
    const tableData = useSelector(makeSelectorTableData(TableNameType.account));
    const isTableLoading = useSelector(makeSelectorTableIsLoading(TableNameType.account));

    const isFastActionPage = useSelector(makeSelectorIsFastActionPage());
    const isFastBasketPage = useSelector(makeSelectorIsFastBasketPage());
    const fastActionCheckedItems = filter(tableData as IPersonalAccount[], (item) => !!item.fastActionChecked);
    const countFastActionChecked = fastActionCheckedItems?.length ?? 0;
    const isOnlyOneAccountSelected = countFastActionChecked === 1 && !!fastActionCheckedItems[0];
    const clientId = isOnlyOneAccountSelected ? String(fastActionCheckedItems[0].id) : '';
    const isFastActionCountShown = !!countBasket && !isFastActionPage && !isTableLoading;
    const isTableFooterShown = isOpen && buttons.length > 0;

    const {
        bills,
        dcRules,
        expenses,
        inquiryCreate,
        mobileCash,
        mobileReport,
        payment,
        reconciliation,
        updateBalance,
        wizardMoneyTransfer,
    } = useTableFooterButtons(
        TableFooterNameType.account,
        undefined,
        isOnlyOneAccountSelected ? String(fastActionCheckedItems[0].id) : undefined,
    );

    const {
        isBtnDisabled,
        getFooterButtonTooltipMsg,
    } = useTableFooterDisabled(
        TableFooterNameType.account,
        undefined,
        undefined,
        isFastBasketPage ? countFastActionChecked : undefined,
    );

    const getTooltipMsg = useCallback((item: IUseTableFooterButtons): string =>
        getFooterButtonTooltipMsg(item.id) || item.tooltipMsg, [getFooterButtonTooltipMsg]);

    const tooltipMsg = getTooltipMsg(inquiryCreate);

    const checkBtnDisabled = useCallback((item: IUseTableFooterButtons) => {
        if (item.id === wizardMoneyTransfer.id) return isBtnDisabled(item.id, 0);
        return isBtnDisabled(item.id);
    }, [isBtnDisabled, wizardMoneyTransfer]);

    const renderBtn = useCallback((item: IUseTableFooterButtons) => (
        <Guard accessKey={item.id} key={item.id}>
            <TableFooterItem
                {...item}
                isDisabled={checkBtnDisabled(item)}
                tooltipMsg={getTooltipMsg(item)}
            />
        </Guard>
    ), [checkBtnDisabled, getTooltipMsg]);

    useEffect(() => {
        dispatch(mobileFileCashSuccessful(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const TableFooter: FC = () => {
        switch (location.pathname) {
            case RoutePath.fastInquiryCreate:
                return (
                    <TableFooterLeftContainerStyled className="table-footer__left-container">
                        <FastInquiryCreateButtons 
                            cancelButtonText={personalAccountTranslation.tableFooter.cancel} 
                            inquiryCreate={inquiryCreate} 
                            isDisabled={!isOnlyOneAccountSelected} 
                            tooltipMsg={tooltipMsg}
                        />
                    </TableFooterLeftContainerStyled>
                );
            case RoutePath.fastAdvanceInvoice:
                return (
                    <>
                        <TableFooterLeftContainerStyled className="table-footer__left-container">
                            <TableFooterFastActionsButtonsStyled>
                                <FastAdvanceInvoiceButtons 
                                    applyButtonTooltip={tooltipMsg}
                                    clientId={clientId}
                                    onCloseModal={() => openRoute(RoutePath.dashboard)}
                                />
                                {isMobileCashFileLoading && <Loader showLoader={true}/>}
                                {
                                    !isMobileCashFileLoading && isMobileCashFileSuccess && (
                                        <Typography variant="body1">
                                            {personalAccountTranslation.tableFooter.mobileCashFileSuccessfulText}
                                        </Typography>
                                    )
                                }
                            </TableFooterFastActionsButtonsStyled>
                        </TableFooterLeftContainerStyled>
                    </>
                );
            case RoutePath.fastPromisedPayment:
                return (
                    <TableFooterLeftContainerStyled className="table-footer__left-container">
                        <FastPromisedPaymentButtons 
                            clientId={clientId}
                            isDisabled={!isOnlyOneAccountSelected}
                            tooltipText={tooltipMsg}
                        />
                    </TableFooterLeftContainerStyled>
                );
            case RoutePath.wizardFastBillsDoc:
            case RoutePath.wizardFastReportDoc:
                return <></>;
            default:
                return (
                    <>
                        <TableFooterLeftContainerStyled
                            className="table-footer__left-container"
                        >
                            <TableFooterRowContainerStyled>
                                {[mobileCash, payment, bills, dcRules].map(renderBtn)}
                            </TableFooterRowContainerStyled>
                            <TableFooterRowContainerStyled>
                                {[inquiryCreate, reconciliation, expenses, wizardMoneyTransfer, updateBalance]
                                    .map(renderBtn)}
                            </TableFooterRowContainerStyled>
                        </TableFooterLeftContainerStyled>
                        <TableFooterRightContainerStyled className="table-footer__right-container">
                            {[mobileReport].map(renderBtn)}
                        </TableFooterRightContainerStyled>
                    </>
                );
        }
    };

    return (
        <TableFooterContainerStyled className="table-footer personal-account-footer">
            {
                !isFastActionPage && 
                <TableFooterShowMenu
                    gtmSectionType={EGTMBasketSectionType.ACCOUNT}
                    isOpen={!!isOpen}
                    onClick={onClick}
                />
            }
            {
                isFastActionCountShown &&
                <TableFooterCounterContainerStyled className="table-footer__counter">
                    <Basket
                        count={countBasket}
                        gtmProps={{
                            sectionType: EGTMBasketSectionType.ACCOUNT,
                            valueType: EGTMBasketValueType.ACCOUNT,
                        }}
                        title={personalAccountTranslation.tableFooter.basketTitle}
                    />
                </TableFooterCounterContainerStyled>
            }
            <TableFooterButtonContainerStyled>
                {isTableFooterShown && <TableFooter />}
            </TableFooterButtonContainerStyled>
        </TableFooterContainerStyled>
    );
};

interface IConnectedProps {
    buttons: ITableReducer[TableNameType]['buttons'];
    countBasket: number | null;
    personalAccountTranslation: IPagePersonalAccountTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    buttons: makeSelectorTableButtons(),
    countBasket: makeSelectorTableBasketCount(TableNameType.account),
    personalAccountTranslation: makeSelectTranslationPagePersonalAccount(),
});

export default connect(mapStateToProps)(PersonalAccountTableFooter);
